import { PlatformCatalogDTO, PublicationStatus, useGetPlatformCatalogById, useUpdatePlatformCatalog } from "@bvhi/backend-api/apis";
import {
  Box,
  Card,
  CardActions,
  CardContent,
  CardHeader,
  FormControl,
  FormControlLabel,
  FormLabel,
  Grid,
  Radio,
  RadioGroup,
  Stack,
  Typography,
} from "@bvhi/ui-base";
import { useParams } from "@tanstack/react-router";
import { Controller, useFormContext, useFormState } from "react-hook-form";
import { useTranslation } from "react-i18next";
import { FormTextFieldName } from "../../../../../../components/common/form-text-field-name";
import { FormTextFieldNotes } from "../../../../../../components/common/form-text-field-notes";
import { FormUpdateLoadingButton } from "../../../../../../components/form/form-update-loading-button";
import { useAuthContext } from "../../../../../../context/use-auth-context";
import { completeStringDate } from "../../../../../../helpers/date";
import { toNarrowedTypeOrNull } from "../../../../../../utils/ts";
import { PlatformCatalogSynchronisationStatusChip } from "../../components/platform-catalog-synchronsation-status-chip";
import { PlatformCatalogStatusChip } from "../../list/components/platform-catalog-status-chip";

export const CatalogUpdateCard = () => {
  const { catalogId } = useParams({ strict: false });
  if (!catalogId) throw new Error("catalogId not found in path");
  const { currentUser } = useAuthContext();
  const { isPending: isUpdatePlatformCatalogPending } = useUpdatePlatformCatalog();
  const { data: getPlatformCatalogByIdResponse, isError } = useGetPlatformCatalogById(currentUser?.organisation?.id ?? "", catalogId);
  const { control } = useFormContext();
  const { isDirty } = useFormState();
  const { t } = useTranslation(["catalogs", "common", "enums"]);

  const catalog = toNarrowedTypeOrNull<PlatformCatalogDTO>(getPlatformCatalogByIdResponse?.data, "id");

  if (!catalog || isError) return;
  return (
    <Card>
      <CardHeader
        subheader={t("catalogs:catalogs.cards.details.subHeader")}
        title={t("catalogs:catalogs.cards.details.title", { name: catalog.name })}
      />
      <CardContent>
        <Stack spacing={2}>
          <FormTextFieldName label={t("catalogs:catalogs.form.fields.catalog.name.label")} />

          <FormTextFieldNotes optional rows={3} />

          <FormControl>
            <FormLabel id="platform-catalog-status-label">
              {t("catalogs:catalogs.datagrid.header.platform.catalog.publicationStatus")}
            </FormLabel>
            <Controller
              control={control}
              name="platformCatalogStatus"
              render={({ field: { onChange, value } }) => (
                <RadioGroup
                  aria-labelledby="platform-catalog-status-label"
                  onChange={(_event, value) => onChange(value)}
                  row
                  value={(value as string) || ""}
                >
                  <FormControlLabel
                    control={<Radio />}
                    label={t("enums:enums.platform.catalog.status.chip.draft")}
                    value={PublicationStatus.DRAFT}
                  />
                  <FormControlLabel
                    control={<Radio />}
                    label={t("enums:enums.platform.catalog.status.chip.published")}
                    value={PublicationStatus.PUBLISHED}
                  />
                </RadioGroup>
              )}
            />
          </FormControl>
        </Stack>
      </CardContent>
      <CardHeader title={t("catalogs:catalogs.cards.details.furtherDetails")} />
      <CardContent restrictContentWidth={false}>
        <Grid alignItems="flex-start" container justifyContent="flex-start" spacing={1}>
          <Grid container md={6} spacing={1} xs={12}>
            <Grid lg={4} xs={12}>
              <Typography>{t("catalogs:catalogs.datagrid.header.platform.catalog.synchronisation.status")}:</Typography>
            </Grid>
            <Grid lg={8} xs={12}>
              <PlatformCatalogSynchronisationStatusChip
                platformCatalogSynchronisationStatus={catalog.platformCatalogSynchronisationStatus}
                size="small"
              />
            </Grid>
            <Grid lg={4} xs={12}>
              <Typography>{t("catalogs:catalogs.datagrid.header.platform.catalog.status")}:</Typography>
            </Grid>
            <Grid lg={8} xs={12}>
              <PlatformCatalogStatusChip catalog={catalog} />
            </Grid>
            <Grid lg={4} xs={12}>
              <Typography>{t("catalogs:catalogs.datagrid.header.active.version")}:</Typography>
            </Grid>
            <Grid lg={8} xs={12}>
              <Typography>{catalog?.activeVersion?.version ?? t("catalogs:catalogs.datagrid.no.active.version")}</Typography>
            </Grid>
            <Grid lg={4} xs={12}>
              <Typography>{t("catalogs:catalogs.datagrid.header.version.count")}:</Typography>
            </Grid>
            <Grid lg={8} xs={12}>
              <Typography>{catalog.versionCount}</Typography>
            </Grid>
          </Grid>

          <Grid container md={6} spacing={1} xs={12}>
            <Grid lg={4} xs={12}>
              <Typography>{t("common:dto.createdAt")}:</Typography>
            </Grid>
            <Grid lg={8} xs={12}>
              <Typography fontStyle="italic">
                {completeStringDate(catalog.createdAt) ?? t("enums:enums.validation.status.no.content")}
              </Typography>
            </Grid>
            <Grid lg={4} xs={12}>
              <Typography>{t("common:dto.updatedAt")}:</Typography>
            </Grid>
            <Grid lg={8} xs={12}>
              <Typography fontStyle="italic">
                {completeStringDate(catalog.updatedAt) ?? t("enums:enums.validation.status.no.content")}
              </Typography>
            </Grid>
          </Grid>
        </Grid>
      </CardContent>
      <CardActions>
        <Box flex={1} />
        <FormUpdateLoadingButton
          disabled={!isDirty}
          entityLabel={t("catalogs:catalogs.catalog")}
          requestActive={isUpdatePlatformCatalogPending}
        />
      </CardActions>
    </Card>
  );
};
